/**
 * R6 — /admin/templates — company template manager (super_admin only).
 *
 * Usa lo stesso endpoint /api/admin/company-templates. Ogni template è un
 * PDF (es. modulo anagrafica) che il MailEditorModal può allegare in
 * fondo alle mail.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  FilePlus2,
  Download,
  Trash2,
  Loader2,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, hasRole } from "../context/AuthContext";
import { api, formatErrorDetail } from "../lib/api";

import { API } from "../lib/backend";

export default function AdminCompanyTemplatesPage() {
  const { user } = useAuth();
  const canManage = hasRole(user, "super_admin");
  const fileRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [segment, setSegment] = useState("Da acquisire");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.companyTemplatesList();
      setItems(d.items || []);
    } catch (e) {
      toast.error(formatErrorDetail(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canManage) reload();
  }, [reload, canManage]);

  if (!canManage) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-[13px]"
           style={{ color: "var(--text-secondary)" }}>
        Accesso riservato al super admin.
      </div>
    );
  }

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!name.trim()) {
      toast.error("Inserisci un nome prima di caricare");
      e.target.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    fd.append("name", name.trim());
    fd.append("description", description.trim());
    fd.append("default_for_profile_segment", segment);
    setUploading(true);
    try {
      await api.companyTemplateUpload(fd);
      toast.success("Template caricato");
      setName("");
      setDescription("");
      await reload();
    } catch (err) {
      toast.error(formatErrorDetail(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const download = (id) => {
    window.open(
      `${API}/admin/company-templates/${id}/download`,
      "_blank",
      "noopener"
    );
  };

  const remove = async (id) => {
    if (!confirm("Eliminare questo template?")) return;
    try {
      await api.companyTemplateDelete(id);
      toast.success("Eliminato");
      await reload();
    } catch (e) {
      toast.error(formatErrorDetail(e));
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6"
         data-testid="admin-templates-page">
      <div>
        <h1 className="text-[22px] font-semibold"
            style={{ color: "var(--text-primary)" }}>
          Template aziendali
        </h1>
        <p className="text-[13px]"
           style={{ color: "var(--text-secondary)" }}>
          Moduli PDF (es. anagrafica nuovo cliente) allegabili alle mail
          inviate dal Mail Editor.
        </p>
      </div>

      {/* Upload card */}
      <section
        className="rounded-md p-4 space-y-3 bg-white"
        style={{ border: "1px solid var(--border-subtle)" }}
        data-testid="upload-card"
      >
        <div className="text-[11px] uppercase tracking-wide font-semibold"
             style={{ color: "var(--text-muted)" }}>
          Nuovo template
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nome (es. Modulo nuovo cliente)"
            className="h-8 px-2 rounded text-[12px]"
            style={{ border: "1px solid var(--border-subtle)" }}
            data-testid="tpl-name"
          />
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Descrizione (opzionale)"
            className="h-8 px-2 rounded text-[12px]"
            style={{ border: "1px solid var(--border-subtle)" }}
            data-testid="tpl-description"
          />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={segment}
            onChange={(e) => setSegment(e.target.value)}
            className="h-8 px-2 rounded text-[12px]"
            style={{ border: "1px solid var(--border-subtle)" }}
            data-testid="tpl-segment"
          >
            <option value="">Nessun default</option>
            <option value="Da acquisire">Default per profilo "Da acquisire"</option>
            <option value="Acquisito">Default per profilo "Acquisito"</option>
          </select>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            onChange={onFile}
            className="hidden"
            data-testid="tpl-file-input"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1 h-8 px-3 rounded text-[12px] font-medium text-white disabled:opacity-40"
            style={{ background: "var(--accent-primary)" }}
            data-testid="tpl-upload-btn"
          >
            {uploading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FilePlus2 className="h-3.5 w-3.5" />
            )}
            Carica PDF
          </button>
        </div>
      </section>

      {/* List */}
      <section data-testid="templates-list-section">
        <div className="text-[11px] uppercase tracking-wide font-semibold mb-2"
             style={{ color: "var(--text-muted)" }}>
          Template esistenti ({items.length})
        </div>
        {loading && (
          <div className="text-[12px] flex items-center gap-1"
               style={{ color: "var(--text-muted)" }}>
            <Loader2 className="h-3 w-3 animate-spin" />
            Caricamento…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div
            className="rounded-md p-6 text-center text-[12px]"
            style={{
              background: "var(--bg-subtle)",
              border: "1px dashed var(--border-subtle)",
              color: "var(--text-muted)",
            }}
          >
            Nessun template. Carica il primo qui sopra.
          </div>
        )}
        {!loading && items.length > 0 && (
          <ul className="space-y-2" data-testid="templates-list">
            {items.map((tpl) => (
              <li
                key={tpl.id}
                className="rounded-md p-3 bg-white flex items-center justify-between gap-3"
                style={{ border: "1px solid var(--border-subtle)" }}
                data-testid={`tpl-${tpl.id}`}
              >
                <div className="min-w-0 flex-1 flex items-center gap-2">
                  <FileText className="h-4 w-4 shrink-0"
                            style={{ color: "var(--text-muted)" }} />
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold truncate"
                         style={{ color: "var(--text-primary)" }}>
                      {tpl.name}
                    </div>
                    <div className="text-[11px]"
                         style={{ color: "var(--text-muted)" }}>
                      {tpl.description || "—"}
                      {tpl.default_for_profile_segment &&
                        ` · default: ${tpl.default_for_profile_segment}`}
                      {` · ${Math.round((tpl.size_bytes || 0) / 1024)} KB`}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => download(tpl.id)}
                    className="h-7 w-7 inline-flex items-center justify-center rounded transition-colors"
                    style={{ color: "var(--text-muted)" }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "var(--bg-subtle)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                    data-testid={`tpl-download-${tpl.id}`}
                    title="Scarica"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(tpl.id)}
                    className="h-7 w-7 inline-flex items-center justify-center rounded transition-colors"
                    style={{ color: "var(--text-muted)" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#FEE2E2";
                      e.currentTarget.style.color = "#B91C1C";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = "var(--text-muted)";
                    }}
                    data-testid={`tpl-delete-${tpl.id}`}
                    title="Elimina"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
